import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faArrowLeft, 
  faClock,
  faTag,
  faUser,
  faChevronLeft,
  faSpinner,
  faExclamationTriangle,
  faHome,
  faNewspaper,
  faCopy,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { 
  faFacebookF, 
  faTwitter, 
  faWhatsapp, 
  faTelegram 
} from '@fortawesome/free-brands-svg-icons';
import '../styles/PostinganDetail.css';

// Helper function untuk mendapatkan API URL
const getApiUrl = () => {
  // Development mode (React dev server on port 3000)
  if (window.location.port === '3000') {
    return 'http://localhost/stikbar_webv2/public';
  }
  // Production mode
  if (window.location.hostname === 'stikesbaramuli.ac.id' || window.location.hostname === 'www.stikesbaramuli.ac.id') {
    return 'https://stikesbaramuli.ac.id';
  }
  return '';
};

// Helper function untuk mendapatkan URL gambar
const getImageUrl = (gambar) => {
  if (!gambar) return '/images/logo.png';
  if (gambar.startsWith('http')) return gambar;
  return `${getApiUrl()}${gambar}`;
};

// Helper function untuk format tanggal
const formatTanggal = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
  return date.toLocaleDateString('id-ID', options);
};

// Helper function untuk estimasi waktu baca
const estimateReadTime = (text) => {
  if (!text) return 1;
  const wordsPerMinute = 200;
  const words = text.split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return minutes < 1 ? 1 : minutes;
};

const PostinganDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [postingan, setPostingan] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchPostingan = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const baseUrl = getApiUrl();
      
      // Fetch single post
      const response = await fetch(`${baseUrl}/api/postingan.php?id=${id}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        setPostingan(result.data);
        
        // Fetch related posts (same category)
        const relatedResponse = await fetch(`${baseUrl}/api/postingan.php?kategori=${result.data.kategori}&limit=4`);
        const relatedResult = await relatedResponse.json();
        
        if (relatedResult.success) {
          // Filter out current post and limit to 3
          const filtered = relatedResult.data
            .filter(p => p.id !== result.data.id && (p.is_active === 1 || p.is_active === '1'))
            .slice(0, 3);
          setRelatedPosts(filtered);
        }
      } else {
        setError('Postingan tidak ditemukan');
      }
    } catch (err) {
      setError('Gagal memuat postingan');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPostingan();
    window.scrollTo(0, 0);
  }, [fetchPostingan]);

  // Helper untuk mendapatkan share URL dengan Open Graph support
  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    // Gunakan share.php untuk social media crawlers agar gambar muncul di preview
    return `${baseUrl}/share.php?id=${id}`;
  };

  const handleShare = (platform) => {
    const shareUrl = getShareUrl(); // URL dengan Open Graph meta tags
    const directUrl = window.location.href; // URL langsung ke React app
    const title = postingan?.judul || 'Postingan STIKES Baramuli';
    const description = postingan?.ringkasan || '';
    
    const shareUrls = {
      // Facebook akan mengambil Open Graph dari share.php
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      // Twitter akan mengambil Twitter Card dari share.php
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`,
      // WhatsApp - gunakan shareUrl agar preview gambar muncul
      whatsapp: `https://wa.me/?text=${encodeURIComponent(title + '\n\n' + description + '\n\n' + shareUrl)}`,
      // Telegram - gunakan shareUrl agar preview gambar muncul
      telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const copyLink = async () => {
    try {
      // Copy share URL agar ketika di-paste, preview gambar bisa muncul
      const shareUrl = getShareUrl();
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Loading state
  if (loading) {
    return (
      <main className="postingan-detail-page">
        <div className="loading-container">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          <p>Memuat postingan...</p>
        </div>
      </main>
    );
  }

  // Error state
  if (error || !postingan) {
    return (
      <main className="postingan-detail-page">
        <div className="error-container">
          <FontAwesomeIcon icon={faExclamationTriangle} size="3x" />
          <h2>{error || 'Postingan tidak ditemukan'}</h2>
          <p>Maaf, postingan yang Anda cari tidak dapat ditemukan.</p>
          <div className="error-actions">
            <button onClick={() => navigate(-1)} className="btn btn-outline">
              <FontAwesomeIcon icon={faArrowLeft} /> Kembali
            </button>
            <Link to="/postingan" className="btn btn-primary">
              <FontAwesomeIcon icon={faNewspaper} /> Lihat Semua Postingan
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const readTime = estimateReadTime(postingan.konten);

  return (
    <main className="postingan-detail-page">
      {/* Breadcrumb */}
      <nav className="breadcrumb-nav">
        <div className="container">
          <ul className="breadcrumb">
            <li>
              <Link to="/"><FontAwesomeIcon icon={faHome} /> Beranda</Link>
            </li>
            <li>
              <Link to="/postingan">Postingan</Link>
            </li>
            <li className="current">
              {postingan.judul.length > 50 ? postingan.judul.substring(0, 50) + '...' : postingan.judul}
            </li>
          </ul>
        </div>
      </nav>

      {/* Article Header */}
      <header className="article-header">
        <div className="container">
          <div className="article-meta-top">
            <span className="article-kategori">
              <FontAwesomeIcon icon={faTag} />
              {postingan.kategori ? postingan.kategori.charAt(0).toUpperCase() + postingan.kategori.slice(1) : 'Umum'}
            </span>
            <span className="article-date">
              <FontAwesomeIcon icon={faCalendarAlt} />
              {formatTanggal(postingan.created_at || postingan.tanggal_mulai)}
            </span>
            <span className="article-read-time">
              <FontAwesomeIcon icon={faClock} />
              {readTime} menit baca
            </span>
          </div>
          
          <h1 className="article-title">{postingan.judul}</h1>
          
          {postingan.ringkasan && (
            <p className="article-excerpt">{postingan.ringkasan}</p>
          )}

          <div className="article-author">
            <div className="author-avatar">
              <FontAwesomeIcon icon={faUser} />
            </div>
            <div className="author-info">
              <span className="author-name">Admin STIKES Baramuli</span>
              <span className="author-role">Humas & Publikasi</span>
            </div>
          </div>
        </div>
      </header>

      {/* Featured Image */}
      <div className="article-featured-image">
        <div className="container">
          <div className="image-wrapper">
            <img 
              src={getImageUrl(postingan.gambar)} 
              alt={postingan.judul}
              onError={(e) => {
                e.target.src = '/images/logo.png';
              }}
            />
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="article-content">
        <div className="container">
          <div className="content-wrapper">
            {/* Share Sidebar */}
            <aside className="share-sidebar">
              <div className="share-sticky">
                <span className="share-label">Bagikan</span>
                <button 
                  className="share-btn facebook" 
                  onClick={() => handleShare('facebook')}
                  title="Bagikan ke Facebook"
                >
                  <FontAwesomeIcon icon={faFacebookF} />
                </button>
                <button 
                  className="share-btn twitter" 
                  onClick={() => handleShare('twitter')}
                  title="Bagikan ke Twitter"
                >
                  <FontAwesomeIcon icon={faTwitter} />
                </button>
                <button 
                  className="share-btn whatsapp" 
                  onClick={() => handleShare('whatsapp')}
                  title="Bagikan ke WhatsApp"
                >
                  <FontAwesomeIcon icon={faWhatsapp} />
                </button>
                <button 
                  className="share-btn telegram" 
                  onClick={() => handleShare('telegram')}
                  title="Bagikan ke Telegram"
                >
                  <FontAwesomeIcon icon={faTelegram} />
                </button>
                <button 
                  className="share-btn copy" 
                  onClick={copyLink}
                  title="Salin Link"
                >
                  <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                </button>
              </div>
            </aside>

            {/* Main Content */}
            <div className="article-body">
              <div 
                className="article-text"
                dangerouslySetInnerHTML={{ 
                  __html: postingan.konten 
                    ? postingan.konten
                        .replace(/\n\n/g, '</p><p>')
                        .replace(/\n/g, '<br/>')
                        .replace(/^/, '<p>')
                        .replace(/$/, '</p>')
                    : ''
                }}
              />

              {/* Tags */}
              <div className="article-tags">
                <span className="tags-label">Tags:</span>
                <Link to={`/postingan?kategori=${postingan.kategori}`} className="tag">
                  {postingan.kategori || 'Umum'}
                </Link>
                <span className="tag">STIKES Baramuli</span>
                <span className="tag">Kesehatan</span>
              </div>

              {/* Share Mobile */}
              <div className="share-mobile">
                <span className="share-label">Bagikan Artikel:</span>
                <div className="share-buttons">
                  <button className="share-btn facebook" onClick={() => handleShare('facebook')}>
                    <FontAwesomeIcon icon={faFacebookF} />
                  </button>
                  <button className="share-btn twitter" onClick={() => handleShare('twitter')}>
                    <FontAwesomeIcon icon={faTwitter} />
                  </button>
                  <button className="share-btn whatsapp" onClick={() => handleShare('whatsapp')}>
                    <FontAwesomeIcon icon={faWhatsapp} />
                  </button>
                  <button className="share-btn telegram" onClick={() => handleShare('telegram')}>
                    <FontAwesomeIcon icon={faTelegram} />
                  </button>
                  <button className="share-btn copy" onClick={copyLink}>
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                    {copied && <span>Tersalin!</span>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="related-posts-section">
          <div className="container">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faNewspaper} /> Postingan Terkait
            </h2>
            <div className="related-posts-grid">
              {relatedPosts.map((post) => (
                <Link to={`/postingan/${post.id}`} key={post.id} className="related-post-card">
                  <div className="related-post-image">
                    <img 
                      src={getImageUrl(post.gambar)} 
                      alt={post.judul}
                      onError={(e) => {
                        e.target.src = '/images/logo.png';
                      }}
                    />
                    <span className="related-post-kategori">
                      {post.kategori ? post.kategori.charAt(0).toUpperCase() + post.kategori.slice(1) : 'Umum'}
                    </span>
                  </div>
                  <div className="related-post-content">
                    <span className="related-post-date">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      {formatTanggal(post.created_at || post.tanggal_mulai)}
                    </span>
                    <h3>{post.judul}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Navigation */}
      <nav className="article-navigation">
        <div className="container">
          <button onClick={() => navigate(-1)} className="nav-btn prev">
            <FontAwesomeIcon icon={faChevronLeft} />
            <span>Kembali</span>
          </button>
          <Link to="/postingan" className="nav-btn all">
            <FontAwesomeIcon icon={faNewspaper} />
            <span>Semua Postingan</span>
          </Link>
        </div>
      </nav>
    </main>
  );
};

export default PostinganDetail;
